import {
  useEffect,
  useState,
  useMemo,
} from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { shallowEqual } from "react-redux";

import BaseDataTable, {
  IColumnConfig,
  IDataRow,
} from "../../../components/ui/baseDataTable";

import SelectCell from "../../../components/ui/baseDataTable/cells/SelectCell";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import { selectChargesByLoanReferenceId, selectAssetsByChargeIds } from "../Logic/chargesSlice";
import { fetchPTVerifications, upsertPTVerifications } from "../Logic/physicalTechnicalVerificationThunk";
import { selectPTVerificationsByChargeIds, selectPTVerificationsStatus, selectPTVerificationsError } from "../Logic/physicalTechnicalVerificationSlice";

import { FormType } from "../../../api/formConfigs";
import { 
  PTVerification, 
  VerificationParentEntityType,
} from "../../../api/physicalTechnicalVerifications";
import FormConfigCell from "./FormConfigCell";
import { 
  VerificationTableSection, 
  VerificationEmptyTableBody, 
  SaveFooter 
} from "./VerificationContainer";
import { getSupportedDocumentTypesForAssets, PT_VERIFICATION_TYPE_LABELS } from "../Logic/ptVerificationMappings";
import { ANOMALY_SELECT_OPTIONS } from "./anomalyOptions";
import { selectFormConfigEntities } from "../Logic/formConfigsSlice";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";

export interface PTVerificationRow extends PTVerification {
  isAdded: boolean;
  isDirty: boolean;
}

interface PhysicalTechnicalVerificationTableProps {
  loanReferenceId: string;
}

export const PhysicalTechnicalVerificationTable: React.FC<
  PhysicalTechnicalVerificationTableProps
> = ({ loanReferenceId }) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const ptStatus = useAppSelector(selectPTVerificationsStatus);
  const ptError = useAppSelector(selectPTVerificationsError);

  const chargesForLoan = useAppSelector(
    (state) => selectChargesByLoanReferenceId(state, loanReferenceId),
    shallowEqual
  );

  const chargeIds = useMemo(() => {
    return chargesForLoan.map((charge) => charge.charge_id);
  }, [chargesForLoan]);

  const ptVerifications = useAppSelector(
    (state) => selectPTVerificationsByChargeIds(state, chargeIds),
    shallowEqual
  );

  const assetsForCharges = useAppSelector((state) =>
    selectAssetsByChargeIds(state, chargeIds)
  );

  const formConfigsMap = useAppSelector(selectFormConfigEntities);

  const chargeOptions = useMemo(() => {
    return chargesForLoan
      .map((charge) => {
        const secId = charge.registry_identifiers.security_interest_id;
        if (!secId) return null;
        return {
          name: secId,
          value: charge.charge_id,
        };
      })
      .filter((opt): opt is { name: string; value: string } => opt !== null);
  }, [chargesForLoan]);

  const [localData, setLocalData] = useState<PTVerificationRow[]>([]);

  useEffect(() => {
    setLocalData(
      ptVerifications.map((row: PTVerification) => ({
        ...row,
        isAdded: false,
        isDirty: false,
      }))
    );
  }, [ptVerifications, loanReferenceId]);

  const isFormDetailsValid = (details: Record<string, any> | undefined, formType?: FormType): boolean => {
    if (!formType) return true;
    const configData = formConfigsMap[formType];
    if (!configData?.configs?.fields) return true;

    const fields = configData.configs.fields;
    const values = details ?? {};

    return fields.every((field) => {
      if (!field.is_mandatory) return true;

      const value = values[field.key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  const hasChanges = useMemo(() => {
    return localData.some((row) => row.isDirty);
  }, [localData]);

  const hasValidationErrors = useMemo(() => {
    return localData
      .filter((row) => row.isDirty)
      .some((row) => {
        if (!row.parent_entity_id || !row.verification_type) {
          return true;
        }

        const isPreloginValid = isFormDetailsValid(
          row.prelogin_details,
          row.verification_type as string as FormType
        );
        const isCurrentValid = isFormDetailsValid(
          row.current_details,
          row.verification_type as string as FormType
        );

        return !isPreloginValid || !isCurrentValid;
      });
  }, [localData, formConfigsMap]);

  const handleRetry = () => {
    if (caseId) {
      dispatch(fetchPTVerifications(caseId));
    }
  };

  const handleAddRow = () => {
    const newRow: PTVerificationRow = {
      verification_id: uuidv4(),
      parent_entity_type: VerificationParentEntityType.CHARGE,
      parent_entity_id: "",
      verification_type: undefined as any,
      prelogin_details: {},
      current_details: {},
      anomaly: undefined,
      isAdded: true,
      isDirty: true,
    };
    setLocalData((prev) => [...prev, newRow]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    setLocalData((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  const handleRowChange = (
    rowIndex: number,
    value: any,
    field: keyof PTVerificationRow
  ) => {
    setLocalData((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) return row;

        const updatedRow = { ...row, isDirty: true };

        if (field === "parent_entity_id") {
          updatedRow.parent_entity_id = value;
          updatedRow.verification_type = undefined as any;
          updatedRow.prelogin_details = {};
          updatedRow.current_details = {};
        } else if (field === "verification_type") {
          updatedRow.verification_type = value;
          updatedRow.prelogin_details = {};
          updatedRow.current_details = {};
        } else {
          (updatedRow as any)[field] = value;
        }

        return updatedRow;
      })
    );
  };

  const handleSave = async () => {
    if (!caseId) return;

    const dirtyRows = localData.filter((row) => row.isDirty);
    if (dirtyRows.length === 0) return;

    try {
      setIsSaving(true);
      const verificationsToUpsert: PTVerification[] = dirtyRows.map(
        ({ isDirty, isAdded, ...rest }) => rest
      );

      await dispatch(
        upsertPTVerifications({
          case_id: caseId,
          request: {
            verifications: verificationsToUpsert,
          },
        })
      ).unwrap();

      showSuccessToast("Physical & Technical verifications saved successfully");
    } catch (error) {
      showErrorToast("Failed to save verifications. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setLocalData(
      ptVerifications.map((row: PTVerification) => ({
        ...row,
        isAdded: false,
        isDirty: false,
      }))
    );
  };

  const getDocumentOptionsForRow = (row: PTVerificationRow) => {
    if (!row.parent_entity_id) return [];

    const associatedAssets = assetsForCharges[row.parent_entity_id] || [];
    const documentTypes = getSupportedDocumentTypesForAssets(associatedAssets);

    return documentTypes.map((type) => ({
      name: PT_VERIFICATION_TYPE_LABELS[type] || type,
      value: type,
    }));
  };

  const dataRows: IDataRow<PTVerificationRow>[] = useMemo(() => {
    return localData.map((row) => ({
      data: row,
      isEditable: true,
      widgets: row.isAdded ? ["add", "delete"] : ["add"],
      onAdd: handleAddRow,
      onDelete: handleDeleteRow,
    }));
  }, [localData]);

  const columnConfigs: IColumnConfig<PTVerificationRow>[] = [
    {
      headerName: "Security",
      field: "parent_entity_id",
      type: "select",
      getValue: (row) => row.parent_entity_id ?? "",
      isEditable: (_, row) => row.isAdded,
      hideFocus: (row) => !row.isAdded,
      onChange: handleRowChange,
      options: chargeOptions,
      selectCellProps: {
        placeholder: "security",
        rootContainerClassName: "min-w-[120px] flex",
        className: "px-0 text-sm font-medium text-[#0A0A0A]",
        labelClassName: "px-0 py-1 min-w-[120px]",
        titleClassName: "font-medium text-[#0A0A0A]",
      },
    },
    {
      headerName: "Type",
      field: "verification_type",
      type: "custom",
      getValue: (row) => row.verification_type ?? "",
      isEditable: (_, row) => row.isAdded && !!row.parent_entity_id,
      hideFocus: (row) => !row.isAdded || !row.parent_entity_id,
      render: (rowIndex, value) => {
        const row = localData[rowIndex];
        const options = getDocumentOptionsForRow(row);
        const isDropdownEditable = row.isAdded && !!row.parent_entity_id;

        if (!row.parent_entity_id) {
          return (
            <div className="py-1 text-sm text-[#A3A3A3] select-none">
              Select type
            </div>
          );
        }

        return (
          <div className="min-w-[140px]">
            <SelectCell
              name="type"
              value={value as string}
              isEditable={isDropdownEditable}
              options={options}
              className="px-0"
              labelClassName="px-0 py-1 text-xs w-full"
              onChange={(updatedVal) => handleRowChange(rowIndex, updatedVal, "verification_type")}
            />
          </div>
        );
      },
    },
    {
      headerName: "Pre-Login State",
      field: "prelogin_details",
      type: "custom",
      getValue: (row) => row.prelogin_details,
      isEditable: true,
      render: (rowIndex, value) => {
        const row = localData[rowIndex];
        return (
          <FormConfigCell
            title="Pre-Login State"
            formType={row.verification_type as string as FormType}
            disabled={!row.verification_type}
            values={value}
            onChange={(updatedDetails) =>
              handleRowChange(rowIndex, updatedDetails, "prelogin_details")
            }
          />
        );
      },
    },
    {
      headerName: "Current State",
      field: "current_details",
      type: "custom",
      getValue: (row) => row.current_details,
      isEditable: true,
      render: (rowIndex, value) => {
        const row = localData[rowIndex];
        return (
          <FormConfigCell
            title="Current State"
            formType={row.verification_type as string as FormType}
            disabled={!row.verification_type}
            values={value}
            onChange={(updatedDetails) =>
              handleRowChange(rowIndex, updatedDetails, "current_details")
            }
          />
        );
      },
    },
    {
      headerName: "Anomaly",
      field: "anomaly",
      type: "select",
      getValue: (row) => row.anomaly ?? "None",
      isEditable: true,
      onChange: handleRowChange,
      options: ANOMALY_SELECT_OPTIONS,
      selectCellProps: {
        placeholder: "anomaly",
        className: "px-0",
        rootContainerClassName: "min-w-[90px] flex items-center",
        labelClassName: "px-0 py-1 text-xs min-w-[90px]",
      },
    },
  ];

  const isLoading = ptStatus === "loading" && !isSaving;

  return (
    <div className="w-full flex flex-col">
      <VerificationTableSection
        title="Physical & Technical Verifications"
        subtitle="Record technical & physical verification details of assets associated with security charges"
      >
        {isLoading ? (
          <CustomLoadingSpinner
            title="Loading Verifications..."
            subtitle="Fetching physical & technical verifications associated with this case."
            minHeight="min-h-[160px]"
          />
        ) : ptError ? (
          <ErrorDisplay
            error="Unable to load physical & technical verifications. Please try again."
            onRetry={handleRetry}
          />
        ) : (
          <BaseDataTable<PTVerificationRow>
            columnConfigs={columnConfigs}
            dataRows={dataRows}
            showDefaultTableBody={dataRows.length === 0}
            defaultTableBody={
              <VerificationEmptyTableBody
                title="No physical & technical records"
                subtitle="Add a verification type to a security to record its physical and technical verification details."
                onAdd={handleAddRow}
              />
            }
          />
        )}
      </VerificationTableSection>

      {hasChanges && (
        <SaveFooter
          isSaving={isSaving}
          disabled={hasValidationErrors}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
        />
      )}
    </div>
  );
};